import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CONTACT_INFO } from "../data";

export default function OmOs() {
  const heroImg = "https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/craftsman-working.jpg";

  return (
    <>
      <Helmet>
        <title>Om Halsnæs Epoxygulve | Din Lokale Gulvspecialist</title>
        <meta
          name="description"
          content="Mød Simon Babiel og teamet bag Halsnæs Epoxygulve. Vi leverer kompromisløs kvalitet, personlig service og slidstærke gulvløsninger i hele Nordsjælland."
        />
        <meta property="og:title" content="Om Halsnæs Epoxygulve | Din Lokale Gulvspecialist" />
        <meta
          property="og:description"
          content="Mød Simon Babiel og teamet bag Halsnæs Epoxygulve. Vi leverer kompromisløs kvalitet, personlig service og slidstærke gulvløsninger i hele Nordsjælland."
        />
        <meta property="og:image" content={heroImg} />
      </Helmet>

      {/* Page Header */}
      <section className="bg-brand-blue-900 text-white pt-48 pb-28 sm:pt-44 sm:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src={heroImg}
            alt="Om os baggrund"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="text-xs font-bold uppercase tracking-widest text-brand-blue-300 mb-3">
              Hvem er vi
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
              Om Halsnæs Epoxygulve
            </h1>
            <p className="text-lg text-brand-blue-100 mt-4 max-w-2xl">
              Lokal forankring, uovertruffen faglig stolthed og fokus på langtidsholdbare gulvløsninger til erhverv og private.
            </p>
          </div>
        </div>
      </section>

      {/* Core Presentation */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left: Copy */}
            <div className="lg:col-span-7 space-y-6">
              <h2 className="text-3xl font-extrabold text-brand-blue-900 tracking-tight">
                Mød Simon Babiel – din personlige gulvpartner
              </h2>
              <p className="text-base text-concrete-600 leading-relaxed">
                Halsnæs Epoxygulve blev grundlagt med en klar vision: At levere fugefrie gulvbelægninger af absolut højeste kvalitet, hvor personlig service, gennemsigtighed og lokalt engagement er i højsædet. Virksomheden ledes af Simon Babiel, som personligt fører tilsyn med og deltager i alle projekter.
              </p>
              <p className="text-base text-concrete-600 leading-relaxed">
                Vi mener, at et godt gulvprojekt starter med den rette rådgivning. Derfor tager vi os altid god tid til at lytte til dine behov, besigtige undergulvet og forklare de forskellige muligheder. Vi anvender udelukkende godkendte, certificerede materialer fra førende europæiske producenter, hvilket sikrer dig et gulv med maksimal levetid og slidstyrke.
              </p>
              <p className="text-base text-concrete-600 leading-relaxed">
                Uanset om du er en stor industrivirksomhed, der skal have renoveret 1.000 kvm produktionsareal, eller en privat boligejer, der ønsker et råt og moderne epoxygulv i garagen, leverer vi samme høje præcision og faglige stolthed.
              </p>
            </div>

            {/* Right: Image & Key Facts */}
            <div className="lg:col-span-5 space-y-6">
              <div className="aspect-4/3 overflow-hidden rounded-2xl shadow-lg bg-concrete-100 border border-concrete-100">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/showroom-floor.jpg"
                  alt="Moderne showroom gulv"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Key Facts Box */}
              <div className="rounded-2xl bg-concrete-50 p-6 border border-concrete-100 space-y-4">
                <h3 className="font-bold text-brand-blue-900">Hurtige fakta om os:</h3>
                <ul className="space-y-3 text-sm text-concrete-700">
                  <li className="flex items-center gap-3">
                    <i className="fa-solid fa-circle-check text-brand-blue-600"></i>
                    <span><strong>Ejer:</strong> Simon Babiel</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="fa-solid fa-circle-check text-brand-blue-600"></i>
                    <span><strong>CVR-nummer:</strong> {CONTACT_INFO.cvr}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="fa-solid fa-circle-check text-brand-blue-600"></i>
                    <span><strong>Specialer:</strong> Epoxy, PU & industrigulve</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <i className="fa-solid fa-circle-check text-brand-blue-600"></i>
                    <span><strong>Geografi:</strong> Halsnæs og hele Nordsjælland</span>
                  </li>
                </ul>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-concrete-50 border-y border-concrete-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-3xl font-extrabold text-brand-blue-900 tracking-tight">
              Vores tre kerneværdier
            </h2>
            <p className="text-base text-concrete-600">
              Disse værdier danner grundlaget for alt, hvad vi gør, og sikrer dig en tryg proces og et perfekt resultat.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Value 1 */}
            <div className="bg-white p-8 rounded-2xl border border-concrete-100 shadow-xs space-y-4 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-blue-50 text-brand-blue-700 text-2xl">
                <i className="fa-solid fa-award"></i>
              </span>
              <h3 className="text-xl font-bold text-brand-blue-900">Kompromisløs Kvalitet</h3>
              <p className="text-sm text-concrete-600 leading-relaxed">
                Vi sparer aldrig på forberedelsen af undergulvet eller tykkelsen af slidlaget. Vi bruger kun de bedste godkendte materialer for at sikre maksimal levetid.
              </p>
            </div>

            {/* Value 2 */}
            <div className="bg-white p-8 rounded-2xl border border-concrete-100 shadow-xs space-y-4 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-blue-50 text-brand-blue-700 text-2xl">
                <i className="fa-solid fa-handshake"></i>
              </span>
              <h3 className="text-xl font-bold text-brand-blue-900">Gennemsigtighed & Tillid</h3>
              <p className="text-sm text-concrete-600 leading-relaxed">
                Vi overholder altid vores aftaler, tidsplaner og priser. Du får et skriftligt, gennemskueligt tilbud uden skjulte gebyrer eller ubehagelige overraskelser.
              </p>
            </div>

            {/* Value 3 */}
            <div className="bg-white p-8 rounded-2xl border border-concrete-100 shadow-xs space-y-4 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-blue-50 text-brand-blue-700 text-2xl">
                <i className="fa-solid fa-comments"></i>
              </span>
              <h3 className="text-xl font-bold text-brand-blue-900">Personlig Rådgivning</h3>
              <p className="text-sm text-concrete-600 leading-relaxed">
                Du taler direkte med Simon Babiel fra start til slut. Det sikrer hurtig kommunikation, færre misforståelser og en løsning, der passer præcis til dig.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA Block */}
      <section className="relative text-white py-24 overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/craftsman-working.jpg"
            alt="Simon Babiel i gang med arbejde"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-brand-dark/60"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold uppercase tracking-tight mb-4">
            Lad os tage en uforpligtende snak om dit projekt
          </h2>
          <p className="text-base text-slate-200 max-w-2xl mx-auto mb-8">
            Simon Babiel rådgiver dig gerne om alt fra farvevalg til skridsikring og udbedring af undergulv.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center bg-brand-orange text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-full hover:bg-brand-orange/90 transition-all"
            >
              Få et tilbud
            </Link>
            <a
              href={`tel:+45${CONTACT_INFO.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-full hover:bg-white/10 transition-all"
            >
              <i className="fa-solid fa-phone"></i>
              Ring direkte til Simon
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
